import styles from './support-report.module.scss';
import { ValueFormatterParams } from 'ag-grid-community';
import { addMonths, startOfMonth } from 'date-fns';
import { Col, Row } from 'reactstrap';
import PageTitle from '../page-title/page-title';
import { useTableConfigHook, UseTableHook } from '../requestUtils';
import { Box, Container, CreateHeaders, currencyHeader, HeaderSizeEnum, minuteToHourConverter, TextField, textHeader, toCurrency } from '@frontend-workspace/react-shared-lib';
import { WebkuaTable } from '@frontend-workspace/webkua-lib';
import { ISummary } from '../_models/summary';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { circularProgressClasses } from '@mui/material';

/* eslint-disable-next-line */
export interface SupportReportProps { }

export function SupportReport(props: SupportReportProps) {
  const { t } = useTranslation();
  const firstOfmonth = (startOfMonth(addMonths(new Date(), -1)));
  const lastOfmonth = (startOfMonth(new Date()));
  const tableConfig = useTableConfigHook("supportReport");
  const { data, onDateChange, dateRange, isLoading, getData }: { data: any, onDateChange: any, dateRange: any, isLoading: boolean, getData: () => void } = UseTableHook<any>("reports/support", { startDate: firstOfmonth, endDate: lastOfmonth, ignoreCompany: true });
  const companies = data ? data.companies : [];
  const summary: ISummary = data.summary;
  const [showNoChanges, setShowNoChanges] = useState<boolean>(false);

  const filteredData = showNoChanges ? companies : companies?.filter((c: any) => c.initialBalance !== c.remainingBalance);
  const minutesAndHoursRenderer = (params: ValueFormatterParams) => minuteToHourConverter(params.value, "0m");

  const headers = CreateHeaders([
    textHeader("companyNumber", t("standard.companyNo"), HeaderSizeEnum.SMALL),
    textHeader("companyName", t("standard.company"), HeaderSizeEnum.LARGE),
    textHeader("contractResponsible", t("standard.contractManager"), HeaderSizeEnum.LARGE),
    textHeader("initialMinutesLeft", t("supportReport.startingBalance"), HeaderSizeEnum.MEDIUM, {
      renderer: minutesAndHoursRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    currencyHeader("initialBalance", t("supportReport.startingBalance"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("addedMinutes", t("standard.access"), HeaderSizeEnum.MEDIUM, {
      renderer: minutesAndHoursRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    currencyHeader("addedBalance", t("standard.access"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("usedMinutes", t("supportReport.production"), HeaderSizeEnum.MEDIUM, {
      renderer: minutesAndHoursRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    currencyHeader("usedBalance", t("supportReport.production"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("remainingMinutes", t("supportReport.finalBalance"), HeaderSizeEnum.MEDIUM, {
      renderer: minutesAndHoursRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    currencyHeader("remainingBalance", t("supportReport.finalBalance"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ]);

  return (
    <Container
      className="container-fluid pt-4 px-0"
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}>
      <Row>
        <Col>
          <Box
            component="div"
            className="pb-5">
            <PageTitle title={t("app.userSupport")} subtitle={`(${t("standard.report")})`} />
            <WebkuaTable
              isLoading={isLoading}
              onSetDateRange={onDateChange}
              onRefresh={getData}
              data={filteredData}
              headers={headers}
              dateRange={dateRange}
              tableConfig={tableConfig}
              tableFilters={[{
                label: t("supportReport.showNoChanges"),
                checked: showNoChanges,
                onChange: (checked: boolean) => setShowNoChanges(checked)
              }]
              }
            />
          </Box>
        </Col>
      </Row>
      {data.summary &&
        <Row>
          <Col md={3}>
            <div className="d-flex flex-column mb-3">
              <TextField
                value={toCurrency(summary.addedBalance)}
                fullWidth
                label={t("standard.access")}
                disabled
                className='mb-3'
              />
              <TextField
                value={toCurrency(summary.usedBalance)}
                fullWidth
                label={t("supportReport.production")}
                disabled
                className='mb-3'
              />
            </div>
          </Col>
          <Col md={3}>
            <div className="d-flex flex-column mb-3">
              <TextField
                value={toCurrency(summary.initialBalance)}
                fullWidth
                label="IB"
                disabled
                className='mb-3'
              />
              <TextField
                value={toCurrency(summary.remainingBalance)}
                fullWidth
                label="UB"
                disabled
                className='mb-3'
              />
              <TextField
                value={toCurrency(summary.income)}
                fullWidth
                label={t("supportReport.income")}
                disabled
                className='mb-3'
              />
            </div>
          </Col>
        </Row>
      }
    </Container>
  );
}

export default SupportReport;